import httpService from './httpService';
import { APIResponse, APIResponseModel } from '../types';
import {
    GetLayoutsRequest,
    GetLayoutsResponse,
    CreateLayoutRequest,
    CreateLayoutResponse,
    GetLayoutPackagesResponse,
    LayoutPackageDetailData,
    ProductListRequest,
    ProductListResponse,
    GeneratePreviewRequest,
    GeneratePreviewResponse,
    LayoutCategory,
    ApplyStoreResult,
    MultiDisplayTypeRelations,
    DisplayTypeRelations,
} from '@/types/pages/layout';
import localforage from 'localforage';

// ─────────────────────────────────────────────────────────────
// Fetch all layouts by corporate ID
// GET /layouts?...
// ─────────────────────────────────────────────────────────────
export const fetchLayoutsByCorporateAPI = async (
    payload: GetLayoutsRequest,
): Promise<APIResponse<GetLayoutsResponse>> => {
    return httpService.get('/layouts', { params: payload });
};

// ─────────────────────────────────────────────────────────────
// Create a new layout
// POST /layout
// ─────────────────────────────────────────────────────────────
export const createLayoutAPI = async (payload: CreateLayoutRequest): Promise<APIResponse<CreateLayoutResponse>> => {
    return httpService.post('/layout', payload);
};

// ─────────────────────────────────────────────────────────────
// Fetch all layout‐packages under a corporate ID
// GET /layout-package/:corporateId
// ─────────────────────────────────────────────────────────────
export const fetchLayoutPackagesByCorporateId = async (
    corporateId: number,
): Promise<APIResponse<GetLayoutPackagesResponse>> => {
    return httpService.get(`/layout-package/${corporateId}`);
};

// ─────────────────────────────────────────────────────────────
// Fetch categories (display types) for a given corporate
// GET /layout-package/:corporateId/category
// ─────────────────────────────────────────────────────────────
export const fetchLayoutCategoriesAPI = async (corporateId: number): Promise<APIResponse<LayoutCategory[]>> => {
    return httpService.get(`/layout-package/${corporateId}/category`);
};

// ─────────────────────────────────────────────────────────────
// Delete a specific version of a layout‐package
// DELETE /layout-package/:corporateId/remove/:layoutPackageId/version/:versionNumber
// ─────────────────────────────────────────────────────────────
export const deleteLayoutPackageAPI = (corporateId: number, layoutPackageId: number, versionNumber: 'all') => {
    return httpService.delete(`/layout-package/${corporateId}/remove/${layoutPackageId}/version/${versionNumber}`);
};

// ─────────────────────────────────────────────────────────────
// Export a layout package (returns a download link)
// POST /layout-package/:corporationId/export/:layoutPackageId
// ─────────────────────────────────────────────────────────────
export async function exportLayoutPackageAPI(corporationId: number, layoutPackageId: number) {
    return httpService.post(`/layout-package/${corporationId}/export/${layoutPackageId}`);
}

// ─────────────────────────────────────────────────────────────
// Fetch a single layout‐package’s detail
// GET /layout-package/:corporateId/detail/:layoutPackageId
// ─────────────────────────────────────────────────────────────
export const fetchLayoutPackageDetailAPI = async (
    corporateId: number,
    layoutPackageId: number,
): Promise<APIResponse<LayoutPackageDetailData>> => {
    return httpService.get(`/layout-package/${corporateId}/detail/${layoutPackageId}`);
};

// ─────────────────────────────────────────────────────────────
// Fetch products (for regenerate‐preview dialog)
// GET /products/list/:corporateId?search=...
// ─────────────────────────────────────────────────────────────
export const fetchProductsListAPI = async (
    corporateId: number,
    params: ProductListRequest,
): Promise<APIResponse<ProductListResponse>> => {
    return httpService.get(`/products/list/${corporateId}`, { params });
};

// ─────────────────────────────────────────────────────────────
// Regenerate layout preview
// POST /layout-package/:corporateId/generate_preview
// ─────────────────────────────────────────────────────────────
export const generatePreviewAPI = async (
    corporateId: number,
    payload: GeneratePreviewRequest,
): Promise<APIResponse<GeneratePreviewResponse>> => {
    return httpService.post(`/layout-package/${corporateId}/generate_preview`, payload);
};

// ─────────────────────────────────────────────────────────────
// Create a new layout‐package
// POST /layout-package/:corporateId/create
// ─────────────────────────────────────────────────────────────
export interface CreateLayoutPackageRequest {
    package_name: string;
    package_description: string;
    create_empty_layout_package: boolean;
    packages: {
        layouts: Array<{
            id: number;
            layout_capacity: number;
            layout_name: string;
            layout_purpose: number;
            sub_layout_pos_size: string;
            layout_fields: string;
        }>;
        display_type_relations: DisplayTypeRelations;
        multi_display_type_relations: MultiDisplayTypeRelations;
        sub_layouts: Array<{
            id: number;
            layout_capacity: number;
            sublayout_name: string;
            sublayout_purpose: number;
            sub_layout_pos_size: string;
            sublayout_fields: string;
        }>;
    };
}
export interface CreateLayoutPackageResponse {
    information: any;
    id: any;
    data: { id: number };
}

export const createLayoutPackageAPI = async (
    corporateId: number,
    payload: CreateLayoutPackageRequest,
): Promise<APIResponse<CreateLayoutPackageResponse>> => {
    return httpService.post(`/layout-package/${corporateId}/create`, payload);
};

// ─────────────────────────────────────────────────────────────
// Update an existing layout‐package
// PUT /layout-package/:corporateId/:layoutPackageId
// ─────────────────────────────────────────────────────────────
export interface UpdateLayoutPackageRequest extends CreateLayoutPackageRequest {}
export interface UpdateLayoutPackageResponse {
    data: { id: number };
}

export const updateLayoutPackageAPI = async (
    corporateId: number,
    layoutPackageId: number,
    payload: UpdateLayoutPackageRequest,
): Promise<APIResponse<UpdateLayoutPackageResponse>> => {
    return httpService.put(`/layout-package/${corporateId}/${layoutPackageId}`, payload);
};

export const appendLayoutPackageAPI = async (
    corporateId: number,
    layoutPackageId: number,
    payload: {
        package_description: string;
        packages: {
            layouts: Array<{
                id: number;
                layout_capacity: number;
                layout_name: string;
                layout_purpose: number;
                sub_layout_pos_size: string;
                layout_fields: string;
            }>;
            display_type_relations: DisplayTypeRelations;
            sub_layouts: Array<{
                id: number;
                layout_capacity: number;
                sublayout_name: string;
                sublayout_purpose: number;
                sub_layout_pos_size: string;
                sublayout_fields: string;
            }>;
            multi_display_type_relations: MultiDisplayTypeRelations;
        };
    },
): Promise<APIResponse<any>> => {
    return httpService.post(`/layout-package/${corporateId}/append/${layoutPackageId}`, payload);
};

export interface CheckLayoutStoresRequest {
    layout_package_id: number;
    version_number?: number | null;
    assigned_stores: number[];
}
export interface CheckLayoutStoresResponse {
    eligible_stores: number[];
    stores_with_existing_lp: number[];
    ineligible_stores: Record<number, number>;
    layout_token: string;
    token_timeout_minutes: number;
}

export const checkLayoutPackageStoresAPI = async (
    corporateId: number,
    payload: CheckLayoutStoresRequest,
): Promise<APIResponse<CheckLayoutStoresResponse>> => {
    return httpService.post<APIResponseModel<CheckLayoutStoresResponse>>(
        `/layout-package/${corporateId}/check`,
        payload,
    );
};

export const applyLayoutPackageAPI = async (
    corporateId: number,
    payload: { layout_token: string },
): Promise<APIResponse<ApplyStoreResult[]>> => {
    return httpService.post(`/layout-package/${corporateId}/apply`, payload);
};

// ─────────────────────────────────────────────────────────────
// Import a layout‐package binary
// POST /layout-package/:corporateId/import/:layoutPackageId
// ─────────────────────────────────────────────────────────────
export const importLayoutPackageAPI = async (
    payload: File | { layout_token: string },
): Promise<APIResponse<{ layout_token?: string }>> => {
    const corporateId = await localforage.getItem<number>('corporation_id');
    const url = `/layout-package/${corporateId}/import`;

    if (payload instanceof File) {
        return httpService.post(url, payload, {
            headers: {
                'Content-Type': payload.type || 'application/octet-stream',
            },
        });
    } else {
        return httpService.post(url, payload);
    }
};
